import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaSearch,
  FaArrowLeft,
  FaFilter,
  FaDownload,
  FaCalendarAlt,
  FaTag,
  FaList,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import {
  DatePicker,
  Select,
  Input,
  Space,
  Button as AntButton,
  Tag,
  Tooltip as AntTooltip,
  Spin,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState, fYear } from "../../../components/shared/GlobalState";
import AssetOutwardView from "./AssetOutwardView";

const AssetOutwardHistoryChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Date filter states
  const [dateRange, setDateRange] = useState([null, null]);
  const [issueTypeFilter, setIssueTypeFilter] = useState(null);
  const [assetNameFilter, setAssetNameFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // For export functionality
  const [exportLoading, setExportLoading] = useState(false);

  const [{ errorLogApi, user }] = useAtom(gbState);
  const [financial_year] = useAtom(fYear);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this Asset?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(id),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const deleteRecord = (id) => {
    let userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };
    axios
      .delete(
        `${process.env.REACT_APP_API_URL}/church/outward/delete/${id}`,
        config
      )
      .then(() => {
        getDataFromServer();
        toast.success("Asset deleted successfully");
      })
      .catch((err) => {
        toast.error("Cannot be deleted");
        errorLogApi(err, "AssetOutwardHistoryChild", "/asset/delete");
      });
  };

  const outwardOptions = [
    { value: 1, label: "Sale" },
    { value: 2, label: "Donated" },
    { value: 3, label: "Write Off" },
    { value: 4, label: "Transferred" },
  ];

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");
    let urlLink = `${process.env.REACT_APP_API_URL}/church/outward/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        const oldData = response.data.issued?.filter(
          (finFilter) =>
            finFilter?.inward_register?.financial_year === financial_year?.label
        );
        const updatedData = oldData.map((data) => ({
          id: data?._id,
          issued_id: data?.outward_register?.issued_id,
          asset_id: data?.outward_register?.asset_id?._id,
          asset_name: data?.outward_register?.asset_id?.asset_name,
          item_code: data?.outward_register?.item_code,
          issued_date: data?.outward_register?.issued_date,
          asset_tag: data?.outward_register?.asset_tag,
          quantity: data?.outward_register?.quantity,
          issue_type: outwardOptions.find(
            (option) =>
              option.value === parseInt(data?.outward_register?.issue_type)
          )?.label,
          issued_rate: data?.outward_register?.issued_rate,
          issued_description: data?.outward_register?.issued_description,
          issuer_id:
            data?.outward_register?.issuer_id?.first_name +
            " " +
            data?.outward_register?.issuer_id?.last_name,
          allocated_to: data?.outward_register?.allocated_person?.person,
          allocated_mobile: data?.outward_register?.allocated_person?.mobile,
          expected_date_of_return:
            data?.outward_register?.allocated_person?.expected_date_of_return,
          comments: data?.outward_register?.allocated_person?.comments,
        }));

        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
        toast.error("Failed to fetch asset data");
      });
  };

  useEffect(() => {
    getDataFromServer();
  }, [financial_year]);

  // Clear all filters
  const clearFilters = () => {
    setDateRange([null, null]);
    setIssueTypeFilter(null);
    setAssetNameFilter("");
  };

  // Export functionality
  const handleExport = async () => {
    try {
      setExportLoading(true);

      // Create CSV content
      const headers = [
        "Item Code",
        "Asset Name",
        "Issued Date",
        "Quantity",
        "Issue Type",
        "Issued Rate",
        "Issuer",
        "Allocated To",
        "Expected Return Date",
      ];

      let csvContent = headers.join(",") + "\n";

      // Use filtered data for export
      filteredData.forEach((row) => {
        const issueType = JSON.parse(row.issue_type)?.label || "";
        const issuedRate = row.issued_rate ? `$${row.issued_rate}` : "";
        const rowData = [
          `"${row.item_code || ""}"`,
          `"${row.asset_name || ""}"`,
          `"${row.issued_date ? row.issued_date.slice(0, 10) : ""}"`,
          `"${row.quantity || ""}"`,
          `"${issueType}"`,
          `"${issuedRate}"`,
          `"${row.issuer_id || ""}"`,
          `"${row.allocated_to || ""}"`,
          `"${
            row.expected_date_of_return
              ? row.expected_date_of_return.slice(0, 10)
              : ""
          }"`,
        ];
        csvContent += rowData.join(",") + "\n";
      });

      // Create download link
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `asset-outward-history-${moment().format("YYYY-MM-DD")}.csv`
      );
      document.body.appendChild(link);

      // Trigger download
      link.click();
      document.body.removeChild(link);

      toast.success("Export completed successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Search term filter
      const matchesSearch = Object.values(item).some((value) => {
        // Check if value is null or undefined before calling toString()
        if (value === null || value === undefined) return false;
        return value
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });

      // Date range filter
      let matchesDateRange = true;
      if (dateRange[0] && dateRange[1] && item.issued_date) {
        const itemDate = moment(item.issued_date);
        const startDate = moment(dateRange[0]);
        const endDate = moment(dateRange[1]);
        matchesDateRange = itemDate.isBetween(startDate, endDate, "day", "[]");
      }

      // Issue type filter
      let matchesIssueType = true;
      if (issueTypeFilter && item.issue_type) {
        try {
          const itemType = JSON.parse(item.issue_type)?.label;
          matchesIssueType = itemType === issueTypeFilter;
        } catch (e) {
          matchesIssueType = false;
        }
      }

      // Asset name filter
      let matchesAssetName = true;
      if (assetNameFilter && item.asset_name) {
        matchesAssetName = item.asset_name
          .toLowerCase()
          .includes(assetNameFilter.toLowerCase());
      }

      return (
        matchesSearch &&
        matchesDateRange &&
        matchesIssueType &&
        matchesAssetName
      );
    });
  }, [data, searchTerm, dateRange, issueTypeFilter, assetNameFilter]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="p-3 w-full">
      <div className="overflow-visible mb-6 bg-white rounded-xl shadow-md">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <div className="flex justify-center items-center mr-4 w-10 h-10 bg-blue-600 rounded-lg">
                <FaList className="text-white" size={20} />
              </div>
              <h1 className="text-xl font-semibold text-gray-800">
                Asset Outward History
              </h1>
            </div>
            <button
              className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft className="mr-2" /> Back
            </button>
          </div>
          <div className="mb-5 border-b border-gray-200"></div>
          <div className="flex gap-2 mb-4">
            <button
              className={`px-4 py-2 rounded-lg border flex items-center ${
                showFilters
                  ? "text-blue-600 bg-blue-50 border-blue-600"
                  : "text-blue-600 border-blue-600 hover:bg-blue-50"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="mr-2" />{" "}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
            <button
              className="flex items-center px-4 py-2 text-green-700 rounded-lg border border-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <Spin size="small" className="mr-2" />
              ) : (
                <FaDownload className="mr-2" />
              )}{" "}
              Export
            </button>
          </div>
          <div className="mb-5">
            <div className="flex gap-4 items-center mb-4">
              <div className="relative w-full">
                <FaSearch className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <input
                  className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {showFilters && (
              <div className="p-5 mb-5 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="flex items-center text-sm font-semibold text-gray-600">
                    <FaFilter className="mr-2" /> Advanced Filters
                  </h3>

                  <div className="flex gap-3">
                    <button
                      onClick={clearFilters}
                      className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 text-sm hover:bg-gray-100"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3">
                  <div>
                    <div className="flex items-center mb-2">
                      <FaCalendarAlt className="mr-2 text-blue-600" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Date Range
                      </span>
                    </div>
                    <DatePicker.RangePicker
                      className="w-full rounded-lg"
                      onChange={(dates) => setDateRange(dates)}
                      value={dateRange}
                      format="YYYY-MM-DD"
                      placeholder={["Start Date", "End Date"]}
                    />
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <FaTag className="mr-2 text-blue-600" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Issue Type
                      </span>
                    </div>
                    <Select
                      allowClear
                      placeholder="Filter by type"
                      className="w-full rounded-lg"
                      onChange={(value) => setIssueTypeFilter(value)}
                      value={issueTypeFilter}
                      options={[
                        { value: "Permanent", label: "Permanent" },
                        { value: "Temporary", label: "Temporary" },
                        { value: "Transfer", label: "Transfer" },
                      ]}
                    />
                  </div>

                  <div>
                    <div className="flex items-center mb-2">
                      <FaList className="mr-2 text-blue-600" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Asset Name
                      </span>
                    </div>
                    <Input
                      placeholder="Filter by asset name"
                      value={assetNameFilter}
                      onChange={(e) => setAssetNameFilter(e.target.value)}
                      className="w-full rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {filteredData.length > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {paginatedData.length}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">{filteredData.length}</span>{" "}
                    records
                    {(dateRange[0] || issueTypeFilter || assetNameFilter) && (
                      <span className="font-medium text-blue-600">
                        {" "}
                        (filtered)
                      </span>
                    )}
                  </p>
                  {filteredData.length > 0 && (
                    <p className="text-sm text-gray-600">
                      Page {page + 1} of{" "}
                      {Math.ceil(filteredData.length / rowsPerPage)}
                    </p>
                  )}
                </div>

                {(dateRange[0] || issueTypeFilter || assetNameFilter) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {dateRange[0] && dateRange[1] && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                        Date: {moment(dateRange[0]).format("MM-DD-YYYY")} -{" "}
                        {moment(dateRange[1]).format("MM-DD-YYYY")}
                        <button
                          onClick={() => setDateRange([null, null])}
                          className="ml-1.5 text-blue-600 hover:text-blue-700"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {issueTypeFilter && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                        Type: {issueTypeFilter}
                        <button
                          onClick={() => setIssueTypeFilter(null)}
                          className="ml-1.5 text-blue-600 hover:text-blue-700"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                    {assetNameFilter && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                        Asset: {assetNameFilter}
                        <button
                          onClick={() => setAssetNameFilter("")}
                          className="ml-1.5 text-blue-600 hover:text-blue-700"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="overflow-hidden bg-white rounded-xl shadow-md">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Action
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Item Code
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Asset Name
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Issued Date
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Quantity
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Issue Type
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Issued Rate
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-sm font-semibold text-left text-gray-600"
                    >
                      Issuer
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row) => (
                      <tr
                        key={row.id}
                        className="transition-colors duration-150 hover:bg-blue-50"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              className="p-1.5 bg-blue-50 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                              onClick={() => {
                                toggleModal();
                                setCurrentRecord(row);
                              }}
                              title="View details"
                            >
                              <FaEye size={14} />
                            </button>
                            {/* <button
                            className="p-1.5 bg-blue-50 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                            onClick={() =>
                              navigate("/assets/inventory/update", { state: row })
                            }
                            title="Edit"
                          >
                            <FaEdit size={14} />
                          </button> */}
                            <button
                              className="p-1.5 bg-red-50 rounded-md text-red-600 hover:bg-red-100 transition-colors"
                              onClick={() => handleDelete(row.id)}
                              title="Delete"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-mono text-sm font-medium">
                            {row.item_code}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.asset_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {row.issued_date
                              ? dayjs(row.issued_date)
                                  .utc()
                                  .format("MM/DD/YYYY")
                              : "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.quantity}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${(() => {
                              try {
                                const issueType = JSON.parse(
                                  row.issue_type
                                )?.label;
                                if (issueType === "Sale")
                                  return "bg-green-50 text-green-700";
                                if (issueType === "Donated")
                                  return "bg-blue-50 text-blue-600";
                                if (issueType === "Write Off")
                                  return "bg-red-50 text-red-700";
                                if (issueType === "Transferred")
                                  return "bg-purple-50 text-purple-700";
                                return "bg-gray-50 text-gray-700";
                              } catch (error) {
                                return "bg-gray-50 text-gray-700";
                              }
                            })()}`}
                          >
                            {(() => {
                              try {
                                return JSON.parse(row.issue_type)?.label || "-";
                              } catch (error) {
                                return row.issue_type || "-";
                              }
                            })()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.issued_rate ? `$${row.issued_rate}` : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.issuer_id}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center">
                        <p className="font-medium text-gray-600">
                          No records found
                        </p>
                        {(dateRange[0] ||
                          issueTypeFilter ||
                          assetNameFilter ||
                          searchTerm) && (
                          <p className="mt-1 text-sm text-gray-500">
                            Try adjusting your filters
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="flex justify-between items-center mt-6 mb-2">
            <div className="flex gap-3 items-center">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="px-2 py-1 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                {[5, 10, 25, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((prev) => prev - 1)}
                className={`px-4 py-2 rounded-lg border text-sm min-w-[100px] ${
                  page === 0
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-blue-600 text-blue-600 hover:bg-blue-50"
                }`}
              >
                Previous
              </button>
              <button
                disabled={
                  paginatedData.length < rowsPerPage ||
                  (page + 1) * rowsPerPage >= filteredData.length
                }
                onClick={() => setPage((prev) => prev + 1)}
                className={`px-4 py-2 rounded-lg border text-sm min-w-[100px] ${
                  paginatedData.length < rowsPerPage ||
                  (page + 1) * rowsPerPage >= filteredData.length
                    ? "border-gray-200 text-gray-400 cursor-not-allowed"
                    : "border-blue-600 text-blue-600 hover:bg-blue-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          {/* Modal */}
          <ErrorBoundaryRoutes route="/assets/inventory" page="ModalView">
            {showModal && (
              <Modal onClose={toggleModal}>
                <AssetOutwardView currentRecord={currentRecord} />
              </Modal>
            )}
          </ErrorBoundaryRoutes>
        </div>
      </div>
    </div>
  );
};

export default AssetOutwardHistoryChild;
